import { Quill } from 'react-quill';
import MagicUrl from "quill-magic-url";

Quill.register("modules/magicUrl", MagicUrl);
const SnowTheme = Quill.import("themes/snow");
const Delta = Quill.import("delta");
const Clipboard = Quill.import("modules/clipboard");
const quillAllowedRegexPattern =/^[A-Za-z0-9\u0300-\u036F .,!#\$%&\*\(\)\-\/'"\+\?<>\_\[\]:;@“”`‘’]+$/;

/* ======================================================
   CUSTOM CLIPBOARD
====================================================== */
class CleanClipboard extends Clipboard {
  onPaste(e) {
    if (!e.clipboardData) {
      super.onPaste(e);
      return;
    }

    e.preventDefault();

    let html = e.clipboardData.getData("text/html");
    let text = e.clipboardData.getData("text/plain");
    html = this.removeCurlyBraces(html);
    text = this.removeCurlyBraces(text);
    const range = this.quill.getSelection(true);

    let delta;

    // 🔹 Unicode underline (t̲h̲i̲s̲)
    const hasUnicodeUnderline = /[\u0331\u0332\u0333]/.test(text);

    if (hasUnicodeUnderline) {
      delta = this.convertUnicodeUnderline(text);
    }
    // 🔹 Google Docs / Word HTML
    else if (html) {
      const fixedHtml = this.fixPastedHtml(html);
      delta = super.convert(fixedHtml); // LET QUILL HANDLE LISTS
      this.cleanDelta(delta);
    }
    // 🔹 Plain text
    else if (text) {
      delta = this.convertPlainTextWithLinks(text);
    }

    if (!delta) return;

    this.quill.updateContents(
      new Delta().retain(range.index).concat(delta),
      "user"
    );

    this.quill.setSelection(range.index + delta.length(), 0, "silent");
  }

  removeCurlyBraces(text = "") {
    return text.replace(/[{}]/g, "");
  }

  /* =============================
     FIX GOOGLE DOCS HTML
  ============================== */
  fixPastedHtml(html) {
    const doc = new DOMParser().parseFromString(html, "text/html");

    // ✅ Convert underline styles to <u>
    doc.querySelectorAll("span").forEach(span => {
      const style = span.getAttribute("style") || "";
      if (style.includes("underline")) {
        const u = doc.createElement("u");
        u.innerHTML = span.innerHTML;
        span.replaceWith(u);
      }
    });

    // ✅ Ensure space after links
    doc.querySelectorAll("a").forEach(anchor => {
      const next = anchor.nextSibling;
      if (
        next &&
        next.nodeType === Node.TEXT_NODE &&
        !next.textContent.startsWith(" ")
      ) {
        next.textContent = " " + next.textContent;
      }
      if (!next) {
        anchor.after(document.createTextNode(" "));
      }
    });

    return doc.body.innerHTML;
  }

  /* =============================
     Unicode underline → real underline
  ============================== */
  convertUnicodeUnderline(text) {
    const delta = new Delta();
    const chars = [...text];

    for (let i = 0; i < chars.length; i++) {
      const char = chars[i];
      const next = chars[i + 1];

      if (next && /[\u0331\u0332\u0333]/.test(next)) {
        delta.insert(char, { underline: true });
        i++;
      } else {
        delta.insert(char);
      }
    }

    return delta;
  }

  /* =============================
     Plain text URLs → links
  ============================== */
  convertPlainTextWithLinks(text) {
    const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
    const delta = new Delta();
    let lastIndex = 0;
    let match;

    while ((match = urlRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        delta.insert(text.slice(lastIndex, match.index));
      }

      const url = match[0];
      const finalUrl = url.startsWith("http") ? url : `https://${url}`;

      delta.insert(url, { link: finalUrl });

      const nextChar = text[match.index + url.length];
      if (nextChar && nextChar !== " ") {
        delta.insert(" ");
      }

      lastIndex = match.index + url.length;
    }

    if (lastIndex < text.length) {
      delta.insert(text.slice(lastIndex));
    }

    return delta;
  }

  /* =============================
     Keep ONLY valid formats
  ============================== */
  cleanDelta(delta) {
    delta.ops.forEach(op => {
      if (!op.attributes) return;

      Object.keys(op.attributes).forEach(attr => {
        if (
          attr === "underline" ||
          attr === "link" ||
          attr === "list" ||
          attr === "indent" ||
          attr === "align"
        ) {
          return;
        }
        delete op.attributes[attr];
      });
    });
  }
}

Quill.register("modules/clipboard", CleanClipboard, true);

/* ======================================================
   DISABLE CUSTOM FONTS
====================================================== */
const Font = Quill.import("formats/font");
Font.whitelist = [];
Quill.register(Font, true);

/* ======================================================
   EXTENDED SNOW THEME (UNCHANGED)
====================================================== */
export class ExtendSnowTheme extends SnowTheme {
    constructor(quill, options) {
        super(quill, options);
        quill.root.style.fontFamily = "Arial, sans-serif";
        // flag that ensure only create/add our custom elements once
        this.tooltipModified = false;
        // listener for adding our custom input for link 'text'
        quill.on("selection-change", (range) => {
            if (this.tooltipModified) return;
            // mark flag
            this.tooltipModified = true;
            let tooltip = quill.theme.tooltip;
            let index; // link start index
            let length; // link text length
            let linkValue; // link href value

            // custom save link text funtion
            const save = () => {
                let text = "";
                let finalLink = "";
                let idx = index ? index : tooltip.quill.selection.lastRange.index;
                let len = length ? length : tooltip.quill.selection.lastRange.length;
                if (idx >= 0 && len >= 0) {
                    text = quill.getText(idx, len);
                }
                if (typeof linkValue === 'string' && linkValue.toLowerCase().startsWith("www")) {
                    finalLink = "https://" + linkValue;
                } else if(typeof linkValue === 'string' && (!(linkValue.toLowerCase().startsWith("www")) && !(linkValue.toLowerCase().startsWith("https")) && !(linkValue.toLowerCase().startsWith("http")))) {
                    finalLink = "https://www." + linkValue;
                } else {
                    finalLink = linkValue;
                }
                let delta = new Delta()
                    .retain(index ? index : tooltip.quill.selection.lastRange.index)
                    .delete(length ? length : tooltip.quill.selection.lastRange.length)
                    .insert(text ? text : linkValue, { link: finalLink })
                quill.updateContents(delta);

                index = null;
                length = null;
            };

            const linkInputLabel = document.createElement("span");
            linkInputLabel.textContent = "Enter link: ";
            tooltip.root.insertBefore(linkInputLabel, tooltip.textbox);
            var placeholder = quill.theme.tooltip;
            var input = placeholder.root.querySelector("input[data-link]");
            input.dataset.link = 'Enter the link';

            // Modify original link textbox
            const textbox = tooltip.textbox;
            const setLinkValue = (e) => {
                linkValue = e.target.value;
            };

            const setLinkValuewWhenFocused = (e) => {
                const range = getSelection();
                if (range) {
                    if (range.type === 'Range') {
                        e.target.value = "";
                    } 
                }
            }

            // quill auto focus this textbox by default, so this ensure we get the value
            textbox.addEventListener("focus", setLinkValuewWhenFocused);
            // if user update link, need to update value
            textbox.addEventListener("input", setLinkValue);

            textbox.addEventListener("keydown", (e) => {
                if (e.key === "Enter") {
                    save();
                    tooltip.root.classList.add("ql-hidden");
                }
            });

            // Hack 'ql-action' button
            const actionBtn = tooltip.root.querySelector(".ql-action");
            actionBtn.addEventListener("click", () => {
                if (tooltip.root.classList.contains("ql-hidden")) {
                    save();
                    return;
                }
                if (tooltip.root.classList.contains("ql-editing")) {
                    index = tooltip.linkRange.index;
                    length = tooltip.linkRange.length;
                }
            });
        });
    }
}

Quill.register("themes/snow", ExtendSnowTheme);
